// Searchable CPV-code picker for tender search profiles.
//
// Selected codes are shown as chips with their official German label (lazy-
// loaded CPV 2008 catalog, see utilities/cpvCatalog.ts). The input searches
// by code prefix or label text; raw codes and prefix wildcards ("9834113*")
// can still be added directly with Enter. CPV codes broaden a profile's
// matches (they are OR-ed with the keywords), which the helper text spells
// out.

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import { CpvEntry, cpvLabel, loadCpvCatalog, searchCpv } from "../../utilities/cpvCatalog";
import FieldHelpButton from "./FieldHelpButton";

interface Properties {
  value: string[];
  onChange: (codes: string[]) => void;
  onHelp?: () => void;
}

const isCodeInput = (input: string) => /^\d{2,8}\*?$/.test(input.trim());

export default function CpvCodePicker(properties: Properties) {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState<CpvEntry[]>([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadCpvCatalog()
      .then((entries) => {
        if (!cancelled) setCatalog(entries);
      })
      .catch(() => {
        /* catalog is best-effort; raw code entry still works */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  const suggestions = useMemo(() => {
    const matches = searchCpv(catalog, query, 30);
    return matches.filter((entry) => !properties.value.includes(entry.code));
  }, [catalog, query, properties.value]);

  const addCode = (code: string) => {
    const trimmed = code.trim();
    if (!trimmed || properties.value.includes(trimmed)) return;
    properties.onChange([...properties.value, trimmed]);
    setQuery("");
    setActiveIndex(0);
  };

  const removeCode = (code: string) => {
    properties.onChange(properties.value.filter((c) => c !== code));
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      if (open && suggestions[activeIndex]) {
        addCode(suggestions[activeIndex].code);
      } else if (isCodeInput(query)) {
        addCode(query);
      }
    }
  };

  const chipTitle = (code: string): string | undefined => {
    if (code.endsWith("*")) {
      const label = cpvLabel(catalog, code);
      return label
        ? `${t("tenderSearchProfiles.cpvPicker.wildcard")} — ${label} …`
        : t("tenderSearchProfiles.cpvPicker.wildcard");
    }
    return cpvLabel(catalog, code);
  };

  const chipText = (code: string): string => {
    if (code.endsWith("*")) return code;
    const label = cpvLabel(catalog, code);
    if (!label) return code;
    return `${code} — ${label.length > 42 ? `${label.slice(0, 42)}…` : label}`;
  };

  const isUnknown = (code: string) =>
    catalog.length > 0 && !code.endsWith("*") && !cpvLabel(catalog, code);

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      <div className="flex items-center gap-1">
        <label className="text-sm font-medium leading-none">
          {t("tenderSearchProfiles.cpvCodes")}
        </label>
        {properties.onHelp && <FieldHelpButton onClick={properties.onHelp} />}
      </div>
      {properties.value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {properties.value.map((code) => (
            <span
              key={code}
              title={chipTitle(code)}
              className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-sm ${
                isUnknown(code)
                  ? "bg-destructive/10 text-destructive"
                  : "bg-secondary text-secondary-foreground"
              }`}
            >
              {chipText(code)}
              {isUnknown(code) && (
                <span className="text-xs">
                  ({t("tenderSearchProfiles.cpvPicker.unknown")})
                </span>
              )}
              <button
                type="button"
                className="inline-flex cursor-pointer items-center justify-center rounded p-0.5 hover:opacity-70"
                aria-label={t("tenderSearchProfiles.cpvPicker.remove")}
                onClick={() => removeCode(code)}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none placeholder:text-muted-foreground focus:border-ring"
          placeholder={t("tenderSearchProfiles.cpvPicker.placeholder")}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(event.target.value.trim().length > 0);
            setActiveIndex(0);
          }}
          onFocus={() => setOpen(query.trim().length > 0)}
          onKeyDown={onKeyDown}
        />
        {open && suggestions.length > 0 && (
          <ul className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-input bg-popover p-1 shadow-md">
            {suggestions.map((entry, index) => (
              <li key={entry.code}>
                <button
                  type="button"
                  className={`flex w-full cursor-pointer items-baseline gap-2 rounded px-2 py-1 text-left text-sm ${
                    index === activeIndex ? "bg-accent" : ""
                  }`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => addCode(entry.code)}
                >
                  <span className="font-mono text-xs text-muted-foreground">{entry.code}</span>
                  <span className="flex-1">{entry.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {t("tenderSearchProfiles.cpvPicker.hint")}
      </p>
    </div>
  );
}
